import { gameGuestSeatSchema, isGuestId, type GameGuestSeat, type GameMeta } from '@awwyeah/shared';

/**
 * The guest seat's two pure halves (WS-23), kept out of the components so both are testable
 * without Yjs or React.
 *
 * A guest writes their chosen name into the match doc — the only doc they may write — and
 * any member with the board open mirrors it up into `GameMeta.guestNames`, so the surfaces
 * that never open a match doc (the games index, the archive, the household timeline) can
 * name them too.
 */

/** Validates whatever is in the match doc's seat slot. Anything malformed is simply absent. */
export function parseGuestSeat(value: unknown): GameGuestSeat | null {
  const parsed = gameGuestSeatSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The mirror step, as a pure function: given a game and the seat a guest wrote, what (if
 * anything) should be written back to the registry? `null` means "nothing to do".
 *
 * **This is a trust boundary.** The seat map lives in a doc the guest can write, so a guest
 * could put any id and any name in it. Two checks make that harmless: the seat is ignored
 * unless its `guestId` is actually one of this game's `playerIds` (so a guest cannot inject
 * a name for a game they are not in), and unless that id is a guest id (so a guest can never
 * rename a *household member* — `guestNames` is keyed by id, and a member id in there would
 * shadow the roster on every surface that resolves through it).
 */
export function mirrorGuestSeatToMeta(meta: GameMeta, seat: GameGuestSeat | null): GameMeta | null {
  if (!seat) return null;
  if (!isGuestId(seat.guestId)) return null;
  if (!meta.playerIds.includes(seat.guestId)) return null;
  if (meta.guestNames?.[seat.guestId] === seat.guestName) return null;
  return {
    ...meta,
    guestNames: { ...meta.guestNames, [seat.guestId]: seat.guestName },
    // `status: 'open'` is the waiting-for-a-guest state; somebody has now joined, so the
    // game is live. Doing it here rather than server-side keeps the "no REST endpoint
    // mutates collaborative state" rule intact (ROADMAP §1.3).
    status: meta.status === 'open' ? 'active' : meta.status,
    updatedAt: new Date().toISOString(),
  };
}

/** Is this game still waiting for its guest to open the link? */
export function isAwaitingGuest(meta: GameMeta): boolean {
  if (meta.status !== 'open') return false;
  return meta.playerIds.some((id) => isGuestId(id) && !meta.guestNames?.[id]);
}
